import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const res = await axios.get(`http://localhost:5000/api/jobs/${id}`);
        setJob(res.data.job);
        setLoading(false);
      } catch (err) {
        setError("Failed to load job details");
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center min-vh-100">
      <div className="text-center">
        <div className="spinner-border text-primary mb-3" style={{width: "3rem", height: "3rem"}} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted fs-5">Loading job details...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="d-flex justify-content-center align-items-center min-vh-100">
      <div className="alert alert-danger d-flex align-items-center" role="alert">
        <i className="bi bi-exclamation-triangle-fill me-2"></i>
        <div>{error}</div>
      </div>
    </div>
  );

  if (!job) return (
    <div className="d-flex justify-content-center align-items-center min-vh-100">
      <div className="text-center">
        <i className="bi bi-emoji-frown display-1 text-muted"></i>
        <h3 className="mt-3 text-dark">Job not found</h3>
        <button className="btn btn-primary mt-3" onClick={handleBack}>
          <i className="bi bi-arrow-left me-2"></i>
          Go Back
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-light min-vh-100 py-5">
      <div className="container">
        {/* Back Button */}
        <button 
          className="btn btn-outline-secondary mb-4" 
          onClick={handleBack}
        >
          <i className="bi bi-arrow-left me-2"></i>
          Back to Jobs
        </button>

        <div className="card shadow-lg border-0 overflow-hidden">
          <div className="row g-0">
            {/* Left Side - Image */}
            <div className="col-lg-6">
              {job.imageUrl ? (
                <div className="h-100 position-relative">
                  <img 
                    src={job.imageUrl} 
                    alt={job.title}
                    className="img-fluid h-100 w-100 object-fit-center"
                    style={{minHeight: "500px"}}
                  />
                  <div className="position-absolute top-0 end-0 m-4">
                    {job.walkin ? (
                      <span className="badge bg-warning text-dark fs-6 px-4 py-2">
                        <i className="bi bi-person-walking me-2"></i>
                        Walk-in Available
                      </span>
                    ) : (
                      <span className="badge bg-success fs-6 px-4 py-2">
                        <i className="bi bi-globe me-2"></i>
                        Online Apply
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-100 d-flex align-items-center justify-content-center bg-secondary bg-opacity-10">
                  <i className="bi bi-image text-muted display-1"></i>
                </div>
              )}
            </div>

            {/* Right Side - Details */}
            <div className="col-lg-6">
              <div className="card-body p-4 p-lg-5">
                {/* Job Title and Company */}
                <div className="mb-4">
                  <h1 className="display-6 fw-bold text-dark mb-3">{job.title}</h1>
                  <div className="d-flex align-items-center mb-4">
                    <i className="bi bi-building text-primary fs-4 me-3"></i>
                    <h2 className="text-primary mb-0">{job.companyName}</h2>
                  </div>
                </div>

                {/* Key Details */}
                <div className="mb-5">
                  <div className="row mb-4">
                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-briefcase-fill text-primary fs-5 me-3"></i>
                        <div>
                          <h6 className="text-muted mb-1">Experience</h6>
                          <p className="fs-5 fw-semibold mb-0">{job.experience}</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-clock-fill text-success fs-5 me-3"></i>
                        <div>
                          <h6 className="text-muted mb-1">Job Type</h6>
                          <p className="fs-5 fw-semibold mb-0">{job.jobType}</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-geo-alt-fill text-info fs-5 me-3"></i>
                        <div>
                          <h6 className="text-muted mb-1">Location</h6>
                          <p className="fs-5 fw-semibold mb-0">{job.location}</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-currency-dollar text-warning fs-5 me-3"></i>
                        <div>
                          <h6 className="text-muted mb-1">Salary</h6>
                          <p className="fs-5 fw-semibold mb-0">{job.salaryRange}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Job Description */}
                <div className="mb-5">
                  <h3 className="h4 fw-bold mb-3">
                    <i className="bi bi-file-text text-primary me-2"></i>
                    Job Description
                  </h3>
                  <div className="bg-light p-4 rounded">
                    <p className="mb-0" style={{lineHeight: "1.8"}}>
                      {job.description || "No description available."}
                    </p>
                  </div>
                </div>

                {/* Walk-in Details */}
                {job.walkin && (
                  <div className="mb-5">
                    <h3 className="h4 fw-bold mb-3 text-warning">
                      <i className="bi bi-calendar-event me-2"></i>
                      Walk-in Interview Details
                    </h3>
                    <div className="bg-warning-subtle p-4 rounded border border-warning">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <div className="d-flex align-items-center">
                            <i className="bi bi-calendar-date text-warning fs-5 me-3"></i>
                            <div>
                              <h6 className="text-warning-emphasis mb-1">Date</h6>
                              <p className="fw-semibold mb-0">
                                {new Date(job.walkin.date).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="d-flex align-items-center">
                            <i className="bi bi-clock text-warning fs-5 me-3"></i>
                            <div>
                              <h6 className="text-warning-emphasis mb-1">Time</h6>
                              <p className="fw-semibold mb-0">
                                {job.walkin.time || "9:00 AM - 5:00 PM"}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex align-items-start">
                            <i className="bi bi-geo-alt text-warning fs-5 me-3 mt-1"></i>
                            <div>
                              <h6 className="text-warning-emphasis mb-1">Venue</h6>
                              <p className="fw-semibold mb-0">{job.walkin.venue}</p>
                              {job.walkin.address && (
                                <p className="text-muted mb-0 small">{job.walkin.address}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Apply Date */}
                {job.applyDate && (
                  <div className="alert alert-danger mb-5">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
                      <div>
                        <h6 className="mb-1">Application Deadline</h6>
                        <p className="mb-0 fw-bold">
                          Apply before: {new Date(job.applyDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex flex-column flex-md-row gap-3">
                  {/* {job.applyLink && (
                    <a
                      href={job.applyLink}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-primary btn-lg flex-fill d-flex align-items-center justify-content-center"
                    >
                      <i className="bi bi-send me-2"></i>
                      Apply Now
                    </a>
                  )} */}
                  {job.notificationLink && (
                    <a
                      href={job.notificationLink}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-success btn-lg flex-fill d-flex align-items-center justify-content-center"
                    >
                      <i className="bi bi-file-earmark-text me-2"></i>
                      View Official Notification
                    </a>
                  )}
                  {/* <button className="btn btn-outline-secondary btn-lg">
                    <i className="bi bi-share me-2"></i>
                    Share
                  </button> */}
                </div>

                {/* Additional Information */}
                {(job.skills || job.qualifications) && (
                  <div className="mt-5 pt-4 border-top">
                    <div className="row">
                      {job.skills && (
                        <div className="col-md-6 mb-4">
                          <h5 className="fw-bold mb-3">Required Skills</h5>
                          <div className="d-flex flex-wrap gap-2">
                            {job.skills.split(',').map((skill, index) => (
                              <span key={index} className="badge bg-primary-subtle text-primary-emphasis px-3 py-2">
                                {skill.trim()}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {job.qualifications && (
                        <div className="col-md-6 mb-4">
                          <h5 className="fw-bold mb-3">Qualifications</h5>
                          <ul className="list-unstyled">
                            {job.qualifications.split(',').map((qual, index) => (
                              <li key={index} className="mb-2">
                                <i className="bi bi-check-circle-fill text-success me-2"></i>
                                {qual.trim()}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetails;